import React, { useState, useEffect, useMemo } from 'react';
import API_BASE_URL from './config';
import DataTable from './components/DataTable/DataTable';
import { USER_MANAGEMENT_COLUMNS } from './constants/tableColumns';
import CompanySettings from './CompanySettings';

function UserManagement({ currentUser, theme, toggleTheme }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddUser, setShowAddUser] = useState(false);
    const [showInactive, setShowInactive] = useState(false);  // Sprint 10: Filter for inactive users
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        confirmPassword: '',
        role: 'viewer',
        email: '',
        full_name: '',
        assigned_region: 'ALL'  // Sprint 16: Default to ALL
    });
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/users`, {
                credentials: 'include'
            });
            const data = await response.json();
            
            if (data.error) {
                setError(data.error);
            } else {
                setUsers(data.users || []);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users');
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        // Validation
        if (!formData.username || !formData.password) {
            setError('Username and password are required');
            return;
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    username: formData.username,
                    password: formData.password,
                    role: formData.role,
                    email: formData.email,
                    full_name: formData.full_name,
                    assigned_region: formData.assigned_region  // Sprint 16
                })
            });

            const data = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                setMessage(`✅ User "${formData.username}" created successfully!`);
                setFormData({
                    username: '',
                    password: '',
                    confirmPassword: '',
                    role: 'viewer',
                    email: '',
                    full_name: '',
                    assigned_region: 'ALL'
                });
                setShowAddUser(false);
                fetchUsers();
            }
        } catch (err) {
            console.error('Error creating user:', err);
            setError('Failed to create user');
        }
    };

    const handleUpdateUser = async (userId) => {
        setError('');
        setMessage('');

        try {
            const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    role: editingUser.role,
                    email: editingUser.email,
                    full_name: editingUser.full_name,
                    assigned_region: editingUser.assigned_region  // Sprint 16
                })
            });

            const data = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                setMessage(`✅ User updated successfully!`);
                setEditingUser(null);
                fetchUsers();
            }
        } catch (err) {
            console.error('Error updating user:', err);
            setError('Failed to update user');
        }
    };

    const handleDeleteUser = async (userId, username) => {
        if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
            return;
        }

        setError('');
        setMessage('');

        try {
            const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            const data = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                setMessage(`✅ User "${username}" deleted successfully!`);
                fetchUsers();
            }
        } catch (err) {
            console.error('Error deleting user:', err);
            setError('Failed to delete user');
        }
    };

    const getRoleBadge = (role) => {
        const badges = {
            'dev': '🛠️ Dev',
            'admin': '🔑 Admin',
            'manager': '📊 Manager',
            'viewer': '👀 Viewer'
        };
        return badges[role] || role;
    };

    const getRoleBadgeClass = (role) => {
        const classes = {
            'dev': 'bg-danger',
            'admin': 'bg-primary',
            'manager': 'bg-success',
            'viewer': 'bg-secondary'
        };
        return classes[role] || 'bg-secondary';
    };

    if (loading) {
        return <div className="text-center mt-5"><div className="spinner-border" /></div>;
    }

    return (
        <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>⚙️ Company & User Management</h2>
                <button 
                    onClick={toggleTheme} 
                    className="theme-toggle"
                    title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                >
                    {theme === 'light' ? '🌙' : '☀️'}
                </button>
            </div>

            {/* Company Settings Section */}
            <CompanySettings currentUser={currentUser} />

            <hr style={{ margin: '40px 0', borderColor: 'var(--border-color)' }} />

            {/* User Management Section */}
            <h2 style={{ marginBottom: '20px' }}>👥 Team Members</h2>

            {message && (
                <div className="alert alert-success alert-dismissible fade show">
                    {message}
                    <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
                </div>
            )}

            {error && (
                <div className="alert alert-danger alert-dismissible fade show">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError('')}></button>
                </div>
            )}

            <div className="mb-3 d-flex justify-content-between align-items-center">
                <button 
                    className="btn btn-primary"
                    onClick={() => setShowAddUser(!showAddUser)}
                >
                    {showAddUser ? '✖ Cancel' : '➕ Add New User'}
                </button>
                
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="showInactiveUsers"
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showInactiveUsers">
                        Show Inactive Users
                    </label>
                </div>
            </div>

            {showAddUser && (
                <div className="card mb-4">
                    <div className="card-body">
                        <h5 className="card-title">Create New User</h5>
                        <form onSubmit={handleCreateUser}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Username *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.username}
                                        onChange={(e) => setFormData({...formData, username: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Role *</label>
                                    <select
                                        className="form-select"
                                        value={formData.role}
                                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                                    >
                                        <option value="viewer">👀 Viewer</option>
                                        <option value="manager">📊 Manager</option>
                                        <option value="admin">🔑 Admin</option>
                                        {currentUser?.role === 'dev' && (
                                            <option value="dev">🛠️ Dev</option>
                                        )}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Password * (min 8 chars)</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                        minLength="8"
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Confirm Password *</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Email</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Full Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.full_name}
                                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Assigned Region 📍</label>
                                    <select
                                        className="form-select"
                                        value={formData.assigned_region}
                                        onChange={(e) => setFormData({...formData, assigned_region: e.target.value})}
                                    >
                                        <option value="ALL">🌎 All Regions (Admin/Manager default)</option>
                                        <option value="AZ">🌵 Arizona</option>
                                        <option value="CO">🏔️ Colorado</option>
                                        <option value="NM">☀️ New Mexico</option>
                                        <option value="FL">🌴 Florida</option>
                                        <option value="GA">🍑 Georgia</option>
                                        <option value="TX">🤠 Texas</option>
                                    </select>
                                    <small className="text-muted">
                                        Users with specific regions will see only jobs from those states by default.
                                    </small>
                                </div>
                            </div>
                            <button type="submit" className="btn btn-success">
                                Create User
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <div className="card">
                <div className="card-body">
                    <h5 className="card-title">
                        All Users ({users.filter(u => showInactive || u.is_active).length}
                        {!showInactive && users.filter(u => !u.is_active).length > 0 && 
                            <span className="text-muted"> / {users.length} total</span>
                        })
                    </h5>
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Username</th>
                                    <th>Role</th>
                                    <th>Full Name</th>
                                    <th>Email</th>
                                    <th>Region 📍</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Last Login</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users
                                    .filter(user => showInactive || user.is_active)
                                    .map((user) => (
                                    <tr key={user.id}>
                                        <td>
                                            <strong>{user.username}</strong>
                                            {user.username === currentUser?.username && (
                                                <span className="badge bg-info ms-2">You</span>
                                            )}
                                        </td>
                                        <td>
                                            {editingUser?.id === user.id ? (
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={editingUser.role}
                                                    onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                                                    disabled={user.role === 'dev' && currentUser?.role !== 'dev'}
                                                >
                                                    <option value="viewer">Viewer</option>
                                                    <option value="manager">Manager</option>
                                                    <option value="admin">Admin</option>
                                                    {currentUser?.role === 'dev' && (
                                                        <option value="dev">Dev</option>
                                                    )}
                                                </select>
                                            ) : (
                                                <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                                                    {getRoleBadge(user.role)}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {editingUser?.id === user.id ? (
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={editingUser.full_name || ''}
                                                    onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                                                />
                                            ) : (
                                                user.full_name || '-'
                                            )}
                                        </td>
                                        <td>
                                            {editingUser?.id === user.id ? (
                                                <input
                                                    type="email"
                                                    className="form-control form-control-sm"
                                                    value={editingUser.email || ''}
                                                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                                                />
                                            ) : (
                                                user.email || '-'
                                            )}
                                        </td>
                                        <td>
                                            {editingUser?.id === user.id ? (
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={editingUser.assigned_region || 'ALL'}
                                                    onChange={(e) => setEditingUser({...editingUser, assigned_region: e.target.value})}
                                                >
                                                    <option value="ALL">🌎 All</option>
                                                    <option value="AZ">🌵 AZ</option>
                                                    <option value="CO">🏔️ CO</option>
                                                    <option value="NM">☀️ NM</option>
                                                    <option value="FL">🌴 FL</option>
                                                    <option value="GA">🍑 GA</option>
                                                    <option value="TX">🤠 TX</option>
                                                </select>
                                            ) : (
                                                <span className="badge bg-info">
                                                    {user.assigned_region === 'ALL' || !user.assigned_region ? '🌎 All' : 
                                                     user.assigned_region === 'AZ' ? '🌵 AZ' :
                                                     user.assigned_region === 'CO' ? '🏔️ CO' :
                                                     user.assigned_region === 'NM' ? '☀️ NM' :
                                                     user.assigned_region === 'FL' ? '🌴 FL' :
                                                     user.assigned_region === 'GA' ? '🍑 GA' :
                                                     user.assigned_region === 'TX' ? '🤠 TX' :
                                                     user.assigned_region}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {user.is_active ? (
                                                <span className="badge bg-success">Active</span>
                                            ) : (
                                                <span className="badge bg-secondary">Inactive</span>
                                            )}
                                        </td>
                                        <td>
                                            <small>{new Date(user.created_at).toLocaleDateString()}</small>
                                        </td>
                                        <td>
                                            <small>
                                                {user.last_login 
                                                    ? new Date(user.last_login).toLocaleString()
                                                    : 'Never'
                                                }
                                            </small>
                                        </td>
                                        <td>
                                            {editingUser?.id === user.id ? (
                                                <>
                                                    <button
                                                        className="btn btn-sm btn-success me-1"
                                                        onClick={() => handleUpdateUser(user.id)}
                                                    >
                                                        ✓ Save
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => setEditingUser(null)}
                                                    >
                                                        ✖ Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        className="btn btn-sm btn-primary me-1"
                                                        onClick={() => setEditingUser({...user})}
                                                        disabled={user.role === 'dev' && currentUser?.role !== 'dev'}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleDeleteUser(user.id, user.username)}
                                                        disabled={
                                                            user.username === currentUser?.username ||
                                                            (user.role === 'dev' && currentUser?.role !== 'dev')
                                                        }
                                                    >
                                                        🗑️ Delete
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="mt-4">
                <h5>Permission Notes:</h5>
                <ul>
                    <li><strong>Dev:</strong> Only Dev users can create/edit/delete other Dev users</li>
                    <li><strong>Self-protection:</strong> You cannot delete your own account</li>
                    <li><strong>Soft delete:</strong> Deleted users are deactivated, not permanently removed</li>
                    <li><strong>Password changes:</strong> Users can change their own password in Settings</li>
                </ul>
            </div>
        </div>
    );
}

export default UserManagement;

